<template>
  <BaseCard type="filled" class="relative flex flex-col p-3">
    <div class="flex flex-row items-center">
      <div class="relative mr-4 h-16 w-16 shrink-0">
        <div class="animate-rotate">
          <BaseShape
            shape="7-sided-cookie"
            class="animate-bouncy-spin text-primary h-full w-full scale-120"
          />
        </div>
        <BaseIcon
          size="32"
          class="text-on-primary absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          >{{ feedbackTopic.icon }}</BaseIcon
        >
      </div>
      <div class="flex min-w-0 shrink grow flex-col">
        <p class="text-body-medium text-on-surface-variant mb-0.5">
          {{ $t('common.assessment') }} · <TimeAgo :date="task.createdAt" />
        </p>
        <h3 class="text-title-large">
          {{ task.title ?? feedbackTopic.cta ?? feedbackTopic.title }}
        </h3>
        <p v-if="plainText" class="text-body-medium text-on-surface-variant mt-1 italic">
          {{ plainText }}
        </p>
        <div class="mt-1.5 flex flex-row items-center">
          <BaseCircularProgressIndictator
            class="mr-1 shrink-0"
            :percentage="(numberOfTandemsWithFeedback / task.surveyTandems.length) * 100"
            :radius="10"
            :thickness="4"
          />
          <span
            class="text-body-medium text-on-surface-variant inline-flex w-full max-w-full flex-row items-center gap-1 overflow-hidden whitespace-nowrap"
          >
            {{ task.surveyTandems.length }} {{ $t('common.coachees', task.surveyTandems.length) }} ·
            <span class="min-w-0 shrink overflow-hidden text-ellipsis whitespace-nowrap">
              {{ feedbackTopic.title }}
            </span>
            · Wird geteilt mit:
            <BaseMemberAvatar class="inline-flex!" size="24" :member="task.owner" />
            <span class="shrink-0">{{ task.owner.firstName }}</span>
          </span>
        </div>
      </div>
      <BaseButtonGroup class="self-center">
        <BaseMenu v-if="menuItems.length > 0" :items="menuItems" v-slot="{ open }">
          <BaseIconButton icon="more_vert" size="M" width="narrow" :force-pressed="open" />
        </BaseMenu>
        <BaseTextButton size="M" color="tonalPrimary" @click="routeToTask" width="wide">{{
          isStarted ? 'Weiter' : 'Starten'
        }}</BaseTextButton>
      </BaseButtonGroup>
    </div>
  </BaseCard>
</template>

<script setup lang="ts">
  import type { FeedbackTask } from '@/modules/task/composables/useFeedbackTasks'
  import { computed, ref } from 'vue'
  import BaseCard from '../base/card/BaseCard.vue'
  import { OpenFeedbackState } from '@/modules/open-feedbacks/composables/useOpenFeedbacks'
  import BaseShape from '../base/shapes/BaseShape.vue'
  import { useUserStore } from '@/stores/user.store'
  import TimeAgo from '../base/time-ago/TimeAgo.vue'
  import { useRouter } from 'vue-router'
  import { useLocalFeedbacks } from '@/core/composables/useLocalFeedbacks'
  import BaseCircularProgressIndictator from '../base/circlular-progress-indicator/BaseCircularProgressIndictator.vue'
  import BaseTextButton from '../base/button/text-button/BaseTextButton.vue'
  import BaseIconButton from '../base/button/icon-button/BaseIconButton.vue'
  import BaseMenu from '../base/menu/BaseMenu.vue'
  import BaseButtonGroup from '../base/button/BaseButtonGroup.vue'
  import BaseIcon from '../base/icon/BaseIcon.vue'
  import type { SingleMenuItem } from '../base/menu/MenuItem'
  import BaseMemberAvatar from '../base/avatar/BaseMemberAvatar.vue'
  import { useWysiwygEditor } from '@/core/composables/useWysiwygEditor'

  const props = defineProps<{
    task: FeedbackTask
  }>()

  const isDone = ref(
    props.task.surveyTandems.every((tandem) => tandem.senderState === OpenFeedbackState.Completed),
  )

  const { user } = useUserStore()

  const feedbackTopic = computed(() => {
    return props.task.feedbackTopics[0]
  })

  const { feedbacksBySurveyId } = useLocalFeedbacks()
  const feedbacks = feedbacksBySurveyId(props.task.surveyId)

  const numberOfTandemsWithFeedback = computed(() => {
    return props.task.surveyTandems.filter((tandem) =>
      feedbacks.value.find((feedback) => feedback.feedbackReceiverId === tandem.receiver.id),
    ).length
  })

  const isStarted = computed(() => {
    return feedbacks.value.length > 0
  })

  const menuItems = computed(() => {
    const items: SingleMenuItem[] = []
    if (props.task.owner.id === user.id) {
      items.push({
        id: 'survey',
        title: 'Standortbestimmung öffnen',
        leadingIcon: 'assignment',
        callback: () => {
          routeToSurvey()
        },
      })
    }

    return items
  })

  const { convertJSONToPlainText } = useWysiwygEditor()

  const plainText = computed(() => {
    if (!props.task.message) return undefined

    const jsonPlainText = convertJSONToPlainText(props.task.message)

    if (!jsonPlainText) return undefined

    return jsonPlainText
  })

  const router = useRouter()

  const routeToTask = () => {
    if (isDone.value) return
    router.push({
      name: 'feedback-multiple',
      params: { surveyId: props.task.surveyId },
    })
  }

  const routeToSurvey = () => {
    if (isDone.value) return
    router.push({
      name: 'assessment',
      params: { surveyTemplateId: props.task.id },
    })
  }
</script>
